import React from 'react';
import { Box, Divider } from '@mui/material';
import { useTimezone } from '@uplift/contexts';
import { DateFormats } from '@uplift/constants';
import { Calendar } from './Calendar/Calendar.component';
import { MobileCalendarEventsList, MobileCalendarEventsListProps } from './MobileCalendarEventsList';
import { Availability } from './Availability';
import { CalendarDateService } from '@uplift/services';
import { CreateAppointment, CreateAppointmentProps } from './CreateAppointment.component';
import { useQueryClient } from '@tanstack/react-query';
import { StickyStackItem } from '../../Layout';
import { ICSEvent } from '@uplift/api';
import { Modal, ModalActions, ModalContent, ModalTitle } from '../../Modal';
import { ICSEventDetails } from '../ScheduleCalendar/ICSEventDetailsPopup.component';
import { Button } from '../../Button';


export type MobileScheduleCalendarProps = {
  today?: string;
  onEventClick: MobileCalendarEventsListProps['onEventClick'];
  getAvailableBookingSlotRange?: CreateAppointmentProps['getAvailableBookingSlotRange'];
};


export const MobileScheduleCalendar: React.FC<MobileScheduleCalendarProps> = (props) => {
  const queryClient = useQueryClient();
  const timezone = useTimezone();
  const today = React.useMemo(() => {
    return props.today
      || CalendarDateService.date().tz(timezone).format(DateFormats.ISO_DATE);
  }, [ props.today, timezone ]);
  const [ date, setDate ] = React.useState(today);
  const [ currentMonth, setCurrentMonth ] = React.useState(today);
  const [ icsEvent, setICSEvent ] = React.useState<null | ICSEvent>(null);

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <StickyStackItem placement="top" order={1}>
        <Box bgcolor="#fff">
          <Calendar
            today={today}
            date={date}
            setDate={setDate}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
          />
          <Divider />
          <Availability
            today={today}
            date={date}
          />
        </Box>
      </StickyStackItem>
      <MobileCalendarEventsList
        today={today}
        date={date}
        onEventClick={props.onEventClick}
        onICSEventClick={(e) => setICSEvent(e)}
      />
      {icsEvent && (
        <Modal open onClose={() => setICSEvent(null)} fullWidth>
          <ModalTitle>{icsEvent.title}</ModalTitle>
          <ModalContent>
            <ICSEventDetails event={icsEvent} />
          </ModalContent>
          <ModalActions>
            <Button color="secondary" fullWidth onClick={() => setICSEvent(null)}>Back to schedule</Button>
          </ModalActions>
        </Modal>
      )}
      {props.getAvailableBookingSlotRange && (
        <StickyStackItem placement="bottom" order={0}>
          <Box bgcolor="#fff">
            <CreateAppointment
              date={date}
              getAvailableBookingSlotRange={props.getAvailableBookingSlotRange}
              reloadEvents={async () => {
                await queryClient.invalidateQueries({ queryKey: [ 'calendar', date ] });
                await queryClient.invalidateQueries({ queryKey: [ 'calendar', 'booked-days' ] });
              }}
            />
          </Box>
        </StickyStackItem>
      )}
    </Box>
  );
};
